/*
 * Copyright (c) 2021 CalAmp Corp. All Rights Reserved
*/
import React, {useCallback, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {AugmentedSmarterTrailerSensorData, SensorTypes} from '@ion/core/src/types/entities';
import {smoothScrollByElement} from '@ion/core/src/utils/BrowserUtils';
import {getAccordionWrapperStyles} from '@ion/dls/src/components/accordion/Accordion';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {TombstoneLoader} from '@ion/dls/src/components/loading/TombstoneLoader';
import EventTime from '@ion/dls/src/components/time/EventTime';
import {VehicleWithTrailerIcon} from '@ion/dls/src/icons/VehicleWithTrailerIcon';
import {themedClassName} from '@ion/dls/src/theme/helpers';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {useThemeContext} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme} from '@ion/dls/src/types/types';
import {useSectionStyles} from './sectionStyles';
import {getTrailerIssues} from './SmarterTrailerUtils';
import TireDataOverview from './TireDataOverview';

export interface TiresSectionProps {
  trailerData: AugmentedSmarterTrailerSensorData;
  onViewTireSensorReport: Function | null;
  sensorUpdateLoading?: boolean;
  isSideSheetOpen?: boolean;
  tooltipContainerId?: string;
  selectedSensor?: SensorTypes;
}

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    detailTombstone : {
      height  : '300px',
      padding : '10px'
    },
    tiresHeader : {
      display        : 'flex',
      justifyContent : 'center',
      alignItems     : 'center'
    },
    tiresHeaderTitle : {
      marginRight : '15px',
      fontFamily  : 'Roboto',
      fontWeight  : 600,
      fontSize    : '14px',
      lineHeight  : '17px',
      color       : dlsColor('black')
    },
    content : {
      padding       : '15px',
      display       : 'flex',
      flexDirection : 'column'
    },
    contentLeft : {
      display        : 'flex',
      justifyContent : 'center',
      alignItems     : 'center'
    },
    contentRight : {
      flex : 1
    },
    notificationIcon : {
      marginTop : 2
    },
    trailerIcon : {
      width     : 157,
      transform : 'rotateZ(90deg)',
      margin    : '-90px 0 -75px 0' // to account for the viewbox whitespace
    },
    footerCtaContainer : {
      textAlign : 'center'
    },
    footerCta : {
      ...dlsTypography('cta'),
      fontSize      : '14px !important',
      letterSpacing : '1px',
      cursor        : 'pointer',
      '& a'         : {
        color                        : dlsColor('aaBlue'),
        textDecoration               : 'none',
        '&:hover, &:active, &:focus' : {
          textDecoration : 'underline'
        }
      }
    },
    warning : {
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'center'
    },
    eventTime : {
      padding : '0px 15px 15px'
    },
    ...getAccordionWrapperStyles(),
    // current theme only - the card already supplies 16px side padding and
    // a 16px gap between its sections, so add that gap alongside the
    // shared content padding override ...
    currentContentGap : {
      gap : '16px'
    }
  };
});

export const tireSectionContent = 'tire-section-content';

/**
 *  Tires section of the smarter trailer content that contains
 *  the tire overview content and trailer icon.
 *
 * ```typescript
 *
 *  import {TiresSection} from '@ion/features/src/smarterTrailer';
 *
 *  <TiresSection asset={selectedAsset}
 *    trailerData={trailerData}
 *    selectedTire='trailerBackLeftInside'
 *    sensorUpdateLoading={isUpdatingSensorData} />
 *
 * ```
 */
export const TiresSection = ({
  trailerData,
  onViewTireSensorReport,
  sensorUpdateLoading,
  selectedSensor
}: TiresSectionProps) => {
  const classes                           = useStyles();
  const sectionClasses                    = useSectionStyles();
  const {isCurrent}                       = useThemeContext();
  const [selectedTire, setSelectedTire]   = useState(null);
  const [hoveredTire, setHoveredTire]     = useState(null);
  const [listItemsOpen, setListItemsOpen] = useState(null);
  const sectionData                       = trailerData[selectedSensor];
  const issues                            = getTrailerIssues(sectionData);
  const messageTime                       = Object.values(sectionData)[0].messageTime;
  const [anchorEl, setAnchorEl]           = useState(null);

  function onTireClick(tire) {
    setListItemsOpen(tire);
    setSelectedTire(tire);
  };

  function onTireMouseOver(tire) {
    setHoveredTire(tire);
    setSelectedTire(tire);
    setAnchorEl(document.getElementById(tire));
    smoothScrollByElement(`tire-grid-${tire}`, 'nearest');
  };

  function onTireMouseOut() {
    setHoveredTire(null);
    setAnchorEl(null);
    setSelectedTire(null);
    selectedTire && smoothScrollByElement(`tire-grid-${selectedTire}`, 'nearest');
  }

  function onItemClick(tire, open) {
    setListItemsOpen(open ? tire : null);
    if (open) {
      setSelectedTire(tire);
      setAnchorEl(document.getElementById(tire));
    }
  }

  if (!sensorUpdateLoading) {
    return (
      <>
        <div id={tireSectionContent}
          className={classnames(
            classes.content,
            isCurrent && sectionClasses.currentContent,
            isCurrent && classes.currentContentGap
          )}>
          <div className={classes.contentLeft}>
            <VehicleWithTrailerIcon vehicleAxles={0}
              trailerAxles={2}
              issues={issues}
              onTireMouseOver={onTireMouseOver}
              onTireMouseOut={onTireMouseOut}
              onTireClick={onTireClick}
              hoveredItem={hoveredTire}
              selectedItem={selectedTire}
              anchorEl={anchorEl}
              sensorType={selectedSensor} />
          </div>
          <div className={classes.contentRight}>
            <TireDataOverview trailerData={sectionData}
              selectedTire={selectedTire}
              hoveredTire={hoveredTire}
              onHighlight={setHoveredTire}
              listItemsOpen={listItemsOpen}
              onItemClick={onItemClick} />
          </div>
        </div>
        <div className={themedClassName(classes.eventTime, sectionClasses.currentContent, isCurrent)}>
          <EventTime eventTime={messageTime} />
        </div>
        {onViewTireSensorReport && selectedSensor === SensorTypes.TPMS && (
          <div className={classes.footerCtaContainer}>
            <TertiaryButton className={classes.footerCta}
              onClick={function() { onViewTireSensorReport(); }}>
              {translate('tracking:assetDetail.viewFullReport')}
            </TertiaryButton>
          </div>
        )}
      </>
    );
  }
  return (
    <div className={classes.detailTombstone}>
      <TombstoneLoader />
    </div>
  );
};

export default TiresSection;
